import { v4 as uuidv4 } from 'uuid'
import { request } from '@remote/api/request'
import type { IErrorRes, IOptions } from '@remote/type'

interface IHeziRequestResponse<T> {
  code: number
  ret: number
  data: T
  msg?: string
}
type IRequestCommonResponse<T> = [IErrorRes | null, IHeziRequestResponse<T>]
const $http = {
  get: async <T>(url: string, params?: object, traceId = '') => {
    const options: IOptions = {
      apiEnv: 'heziProduction',
      method: 'GET',
      headers: { 'X-Trace-Id': traceId },
    }
    return request(url, params, options).then((response: IRequestCommonResponse<T>) => {
      const [err, result] = response
      if (err) {
        const { error, error_description, msg } = err
        const errMessage = `${msg}(#${error}: ${error_description})`
        throw new Error(errMessage)
      }

      return result
    })
  },
  post: async <T>(url: string, data?: object, traceId = '') => {
    const options: IOptions = {
      apiEnv: 'heziProduction',
      method: 'POST',
      headers: { 'X-Trace-Id': traceId },
    }
    return request(url, data, options).then((response: IRequestCommonResponse<T>) => {
      const [err, result] = response
      if (err) {
        const { error, error_description, msg } = err
        const errMessage = `${msg}(#${error}: ${error_description})`
        throw new Error(errMessage)
      }

      return result
    })
  },
}

interface IXOSPackageInfo {
  /** 下载链接 */
  url: string
  /** 版本号 */
  ver: number
}
async function getXOSPackage (publish_type: string, traceId = uuidv4()): Promise<IXOSPackageInfo> {
  const url = '/api/v1/package/get_xos_package'

  return $http.get<IXOSPackageInfo>(url, { publish_type }, traceId).then(response => {
    console.log('[API][getXOSPackage][response]:', response)
    if (response.code !== 200) {
      throw new Error(response.msg || '获取xos包信息失败')
    }
    return response.data
  })
}

interface IGetLastestVersionParams {
  traceId?: string
  /** 应用运行环境 */
  runtime: string
  /** 应用发布类型: dev | stable */
  publish_type: string
}
interface IGetVersionItem {
  app_id: string
  display_name: string
  icon: string
  type: 'paas' | 'saas'
  ver: number
}
interface ILastestVersion {
  name: string
  version: number
}
interface IVersionInfo {
  xos: ILastestVersion
  xl_gx: ILastestVersion
  xl_disk: ILastestVersion
  xl_shell: ILastestVersion
  xl_dcdn: ILastestVersion
  xl_thumb: ILastestVersion
  xl_thunder: ILastestVersion
  /** 索引服务 */
  xl_scrape: ILastestVersion
  xl_backer: ILastestVersion
  [app: string]: ILastestVersion
}

interface IGetAppListResult {
  list: IGetVersionItem[]
  version?: number
}
/**
 * 获取设备所有应用服务列表（包括名称、id、最新版本等信息）
 */
async function getXOSlastestVersion (params: IGetLastestVersionParams, traceId = uuidv4()) {
  const { runtime, publish_type } = params
  const url = '/api/v2/package/get_app_list'

  return $http.get<IGetAppListResult>(url, { runtime, publish_type }, traceId).then(response => {
    console.log('[API][getXOSlastestVersion][response]:', response)
    const { code, msg, data } = response
    if (code !== 200) {
      throw new Error(msg)
    }
    const list = Array.isArray(data.list) ? data.list : []
    const versionCode = data.version || 0

    const versions = list.reduce<{ [appid: string]: ILastestVersion }>((vers, app) => {
      vers[app.app_id] = {
        name: app.display_name,
        version: app.ver,
      }
      return vers
    }, {})

    return { versions, ver: versionCode }
  })
}

export async function getLastestServerVersions (params: IGetLastestVersionParams, traceId?: string) {
  return Promise.all([
    getXOSPackage(params.publish_type, traceId),
    getXOSlastestVersion(params, traceId),
  ]).then(response => {
    const [xosVersion, versionsWithoutXOS] = response
    const result: IVersionInfo = ({
      xos: { name: '迅雷盒子系统', version: xosVersion.ver },
      ...versionsWithoutXOS.versions,
    } as IVersionInfo)
    return { versions: result, ver: versionsWithoutXOS.ver }
  })
}
